<script setup>
import { computed, reactive, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';

const props = defineProps({
  type: {
    type: String,
    required: true,
    validator: value => ['make', 'model', 'trim', 'color'].includes(value),
  },
  parentId: {
    type: [String, Number],
    default: '',
  },
});

const emit = defineEmits(['created']);

const { t } = useI18n();
const store = useStore();

const dialogRef = ref(null);
const uiFlags = useMapGetter('wgptCarCatalog/getUIFlags');

const state = reactive({
  name: '',
  aliases: [],
});

const localeOptions = computed(() =>
  ['en', 'ar', 'es'].map(value => ({
    value,
    label: t(`WGPT_CAR_LISTINGS.CATALOG.LOCALES.${value}`),
  }))
);

const isConfirmDisabled = computed(() => !state.name.trim());

const addAliasRow = () => {
  state.aliases.push({ name: '', locale: '' });
};

const removeAliasRow = index => {
  state.aliases.splice(index, 1);
};

const resetState = () => {
  state.name = '';
  state.aliases = [];
};

const buildPayload = () => {
  const payload = {
    type: props.type,
    name: state.name.trim(),
    aliases: state.aliases
      .filter(row => row.name.trim())
      .map(row => ({ name: row.name.trim(), locale: row.locale || null })),
  };
  if (props.type === 'model') payload.wgpt_car_make_id = props.parentId;
  if (props.type === 'trim') payload.wgpt_car_model_id = props.parentId;
  return payload;
};

const handleConfirm = async () => {
  if (isConfirmDisabled.value) return;
  try {
    const item = await store.dispatch(
      'wgptCarCatalog/createEntry',
      buildPayload()
    );
    if (item) {
      useAlert(t('WGPT_CAR_LISTINGS.CATALOG.CREATE_DIALOG.SUCCESS'));
      emit('created', item);
      resetState();
      dialogRef.value?.close();
    }
  } catch (error) {
    useAlert(
      error?.message || t('WGPT_CAR_LISTINGS.CATALOG.CREATE_DIALOG.ERROR')
    );
  }
};

const open = () => dialogRef.value?.open();

defineExpose({ open });
</script>

<template>
  <Dialog
    ref="dialogRef"
    :title="t(`WGPT_CAR_LISTINGS.CATALOG.CREATE_DIALOG.TITLE.${type}`)"
    :description="t('WGPT_CAR_LISTINGS.CATALOG.CREATE_DIALOG.DESCRIPTION')"
    :confirm-button-label="t('WGPT_CAR_LISTINGS.CATALOG.CREATE_DIALOG.CONFIRM')"
    :disable-confirm-button="isConfirmDisabled"
    :is-loading="uiFlags.isCreating"
    overflow-y-auto
    @confirm="handleConfirm"
  >
    <div class="flex flex-col gap-4">
      <Input
        v-model="state.name"
        :label="t('WGPT_CAR_LISTINGS.CATALOG.CREATE_DIALOG.NAME_LABEL')"
        :placeholder="
          t(`WGPT_CAR_LISTINGS.CATALOG.CREATE_DIALOG.NAME_PLACEHOLDER.${type}`)
        "
      />
      <div class="flex flex-col gap-2">
        <div class="flex flex-col">
          <span class="text-sm font-medium text-n-slate-12">
            {{ t('WGPT_CAR_LISTINGS.CATALOG.CREATE_DIALOG.ALIASES_LABEL') }}
          </span>
          <span class="text-xs text-n-slate-11">
            {{ t('WGPT_CAR_LISTINGS.CATALOG.CREATE_DIALOG.ALIASES_HINT') }}
          </span>
        </div>
        <div
          v-for="(row, index) in state.aliases"
          :key="index"
          class="flex items-center gap-2"
        >
          <div class="flex-1">
            <Input
              v-model="row.name"
              :placeholder="
                t('WGPT_CAR_LISTINGS.CATALOG.ALIAS_NAME_PLACEHOLDER')
              "
            />
          </div>
          <div class="w-36">
            <ComboBox
              v-model="row.locale"
              :options="localeOptions"
              :placeholder="t('WGPT_CAR_LISTINGS.CATALOG.LOCALES.any')"
            />
          </div>
          <Button
            icon="i-lucide-x"
            variant="ghost"
            color="slate"
            size="sm"
            type="button"
            @click="removeAliasRow(index)"
          />
        </div>
        <Button
          :label="t('WGPT_CAR_LISTINGS.CATALOG.CREATE_DIALOG.ADD_ALIAS')"
          icon="i-lucide-plus"
          variant="ghost"
          color="blue"
          size="sm"
          type="button"
          class="self-start"
          @click="addAliasRow"
        />
      </div>
    </div>
  </Dialog>
</template>
